/**
 * FILE: apps/hub/src/hooks/useMutationWithToast.ts
 * PURPOSE: Reusable mutation wrapper that handles toast notifications and cache invalidation.
 * AGENT_CONTEXT: Use this for 80% of simple mutations. Domain-specific hooks compose this for complex cases.
 * INVARIANTS: Never change toast messages or invalidate keys without verifying consumers.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { toast } from 'sonner'

export interface UseMutationWithToastOptions<TData, TError, TVariables> {
  mutationFn: (variables: TVariables) => Promise<TData>
  successMessage: string
  errorMessage?: string
  invalidateKeys?: (() => readonly unknown[])[]
  onSuccess?: (data: TData, variables: TVariables) => void | Promise<void>
  onError?: (error: TError, variables: TVariables) => void
}

export function useMutationWithToast<TData = unknown, TError extends Error = Error, TVariables = void>(
  options: UseMutationWithToastOptions<TData, TError, TVariables>,
) {
  const qc = useQueryClient()

  const mutation = useMutation<TData, TError, TVariables>({
    mutationFn: options.mutationFn,
    onSuccess: async (data, variables) => {
      if (options.successMessage) toast.success(options.successMessage)

      if (options.invalidateKeys && options.invalidateKeys.length > 0) {
        await Promise.all(
          options.invalidateKeys.map((keyFactory) => qc.invalidateQueries({ queryKey: keyFactory() as readonly unknown[] })),
        )
      }

      await options.onSuccess?.(data, variables)
    },
    onError: (error, variables) => {
      toast.error(options.errorMessage ?? (error as Error).message ?? 'Something went wrong')
      options.onError?.(error as TError, variables)
    },
  })

  return mutation as UseMutationResult<TData, TError, TVariables>
}
